import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useViewedChallenges } from './useViewedChallenges';

export function useChallengeState(user) {
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
const { viewedChallenges, markMultipleAsViewed } = useViewedChallenges(user?.id);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const now = new Date().toISOString();
        
        // Fetch active challenges within time window
        const allChallenges = await base44.entities.Challenge.filter({
          is_active: true
        });

        const timeChallenges = allChallenges.filter(c =>
          new Date(c.start_at) <= new Date(now) &&
          new Date(c.end_at) >= new Date(now)
        );

        setActiveChallenges(timeChallenges);

        // Fetch user progress
        const progress = await base44.entities.UserChallengeProgress.filter({
          user_id: user.id
        });

        const progressMap = {};
        progress.forEach(p => {
          progressMap[p.challenge_id] = p;
        });
        setUserProgress(progressMap);
      } catch (error) {
        console.error('Failed to fetch challenge state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Subscribe to challenge changes
    const unsubscribeChallenge = base44.entities.Challenge.subscribe((event) => {
      fetchData();
    });

    // Subscribe to progress changes
    const unsubscribeProgress = base44.entities.UserChallengeProgress.subscribe((event) => {
      if (event.data?.user_id === user.id) {
        fetchData();
      }
    });

    return () => {
      unsubscribeChallenge();
      unsubscribeProgress();
    };
  }, [user?.id]);

  // Calculate unviewed challenges
  const unviewedChallenges = activeChallenges.filter(c =>
    hasNotParticipated(c.id, userProgress) && !viewedChallenges.includes(c.id)
  );

  return { 
    activeChallenges, 
    userProgress, 
    isLoading,
    unviewedChallenges,
    unviewedCount: unviewedChallenges.length,
    markChallengesAsViewed: markMultipleAsViewed
  };
}

// Helper to check if user has not participated in a challenge
export function hasNotParticipated(challengeId, userProgress) {
  const progress = userProgress[challengeId];
  return !progress || (progress.progress_value === 0 && !progress.is_completed);
}

// Helper to get current day key (YYYY-MM-DD)
export function getDayKey() {
  return new Date().toISOString().split('T')[0];
}

// Helper to check if message was dismissed today
export function wasDismissedToday(dismissedDate) {
  if (!dismissedDate) return false;
  return getDayKey() === dismissedDate.split('T')[0];
}